import { useCallback } from 'react';

import { useGetInspectionPicturesLazyQuery } from 'types/atlas-graphql';
import { useHistory } from 'utils/router';

import { type ComparePicturesParams } from './HorizonDamageImage/types';
import { getComparePicturesPath, getMatchingPictureId } from './HorizonDamageImage/utils';

export const useCompareNavigation = () => {
  const [getInspectionPictures, { loading }] = useGetInspectionPicturesLazyQuery({
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
  });
  const { push } = useHistory();

  const comparePictures = useCallback(
    async ({ previousDamageInfo, previousInspectionInfo }: ComparePicturesParams) => {
      // Previous damage flow
      if (previousDamageInfo) {
        const { currentPictureId, currentObservationId, previousPictureId, previousObservationId } =
          previousDamageInfo;

        if (!currentPictureId || !currentObservationId) {
          return;
        }

        push(
          getComparePicturesPath({
            currentPictureId,
            currentObservationId,
            previousPictureId,
            previousObservationId,
          }),
          { useModal: true }
        );

        return;
      }

      // Previous inspection flow
      if (!previousInspectionInfo) {
        return;
      }

      try {
        const {
          inspectionId,
          blade,
          bladeSide,
          radialDistance,
          currentPictureId,
          currentObservationId,
        } = previousInspectionInfo;

        const { data } = await getInspectionPictures({
          variables: {
            taskId: inspectionId,
            blade,
            bladeSide,
          },
        });

        const previousPictureId = getMatchingPictureId(data?.pictures, radialDistance);

        push(
          getComparePicturesPath({
            currentPictureId,
            currentObservationId,
            previousPictureId,
            emptyPreviousPicture: !previousPictureId,
          }),
          { useModal: true }
        );
      } catch (err) {
        console.error(err);
      }
    },
    [getInspectionPictures, push]
  );

  return {
    comparePictures,
    loading,
  };
};
